import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useCompanyPlan } from '../hooks/useCompanyPlan';
import Layout from '../components/Layout';
import {
  JOB_TYPES, CATEGORIES, BENEFITS,
  CURRENCIES, SKILLS_LIST
} from '../lib/constants';
import {
  Briefcase, MapPin, DollarSign,
  Clock, ChevronRight, ChevronLeft,
  CheckCircle, AlertCircle, Plus, X,
  Lock, Star, Zap
} from 'lucide-react';

const STEPS = ['Basic Info', 'Compensation', 'Requirements', 'Preview'];

export default function PostJob() {
  const { company, refreshCompany } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const { plan, can } = useCompanyPlan(company, refreshCompany);

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [skillInput, setSkillInput] = useState('');
  const [respInput, setRespInput] = useState('');
  const [reqInput, setReqInput] = useState('');

  const [form, setForm] = useState({
    title: '',
    category: '',
    job_type: '',
    department: '',
    description: '',
    responsibilities: [],
    requirements: [],
    skills_required: [],
    salary_min: '',
    salary_max: '',
    salary_currency: 'USD',
    salary_period: 'monthly',
    is_salary_visible: true,
    benefits: [],
    location_city: '',
    location_country: '',
    work_hours: '',
    working_days: 'Mon-Fri',
    deadline: '',
    target_degree: 'any',
    target_year: 'any',
    target_country: 'any',
    experience_required: 'fresher',
    slots_available: 1,
    is_urgent: false,
    is_featured: false,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (editId) loadJob();
  }, [editId]);

  const loadJob = async () => {
    const { data } = await supabase
      .from('job_listings').select('*')
      .eq('id', editId).maybeSingle();
    if (data) setForm(prev => ({ ...prev, ...data }));
  };

  // ── Slot check ───────────────────────────────────
  const slotsUsed = company?.slots_used || 0;
  const slotsTotal = plan.jobSlots === -1 ? 9999 : plan.jobSlots;
  const hasSlots = editId || slotsUsed < slotsTotal;

  const validateStep = () => {
    setError('');
    if (step === 0) {
      if (!form.title) { setError('Job title required'); return false; }
      if (!form.category) { setError('Category required'); return false; }
      if (!form.job_type) { setError('Job type required'); return false; }
      if (!form.description || form.description.length < 50) {
        setError('Description must be at least 50 chars'); return false;
      }
      if (!form.deadline) { setError('Deadline required'); return false; }
      if (!form.location_city) { setError('City required'); return false; }
      if (!form.location_country) { setError('Country required'); return false; }
    }
    if (step === 2) {
      if (form.skills_required.length === 0) {
        setError('Add at least one required skill'); return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    if (!hasSlots) {
      setError('No job slots remaining. Upgrade your plan.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = {
        ...form,
        company_id: company.id,
        salary_min: form.salary_min ? parseFloat(form.salary_min) : null,
        salary_max: form.salary_max ? parseFloat(form.salary_max) : null,
        slots_available: parseInt(form.slots_available) || 1,
        is_active: true,
        slots_filled: 0,
        views_count: 0,
        applications_count: 0,
        // Featured only if plan allows
        is_featured: can.featuredJobs ? form.is_featured : false,
        is_targeted: can.priorityPlacement ? form.is_targeted : false,
        target_countries: (can.priorityPlacement && form.is_targeted && form.target_countries_text)
          ? form.target_countries_text.split(',').map(c => c.trim()).filter(Boolean)
          : null,
      };

      if (editId) {
        await supabase.from('job_listings').update(data).eq('id', editId);
      } else {
        await supabase.from('job_listings').insert(data);
        await supabase.from('companies')
          .update({ slots_used: slotsUsed + 1 })
          .eq('id', company.id);
        await refreshCompany();
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addChip = (field, val, setVal) => {
    const v = val.trim();
    if (!v) return;
    if (!form[field].includes(v)) set(field, [...form[field], v]);
    setVal('');
  };

  const removeChip = (field, idx) =>
    set(field, form[field].filter((_, i) => i !== idx));

  const toggleBenefit = (b) => {
    if (form.benefits.includes(b))
      set('benefits', form.benefits.filter(x => x !== b));
    else
      set('benefits', [...form.benefits, b]);
  };

  return (
    <Layout>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1E293B' }}>
            {editId ? 'Edit Job' : 'Post a New Job'}
          </h1>
          <div style={{
            display: 'flex', alignItems: 'center',
            gap: 10, marginTop: 6, flexWrap: 'wrap',
          }}>
            <span style={{ color: '#94A3B8', fontSize: 13 }}>
              {plan.badge} {plan.name} Plan
            </span>
            <span style={{ color: '#E2E8F0' }}>·</span>
            <span style={{
              fontSize: 13,
              color: hasSlots ? '#10B981' : '#EF4444',
              fontWeight: 600,
            }}>
              {plan.jobSlots === -1
                ? '∞ unlimited slots'
                : `${slotsTotal - slotsUsed} slot${slotsTotal - slotsUsed !== 1 ? 's' : ''} remaining`}
            </span>
            {!editId && (
              <div style={{
                height: 6, flex: 1, maxWidth: 120,
                background: '#F1F5F9', borderRadius: 3,
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: plan.jobSlots === -1
                    ? '20%'
                    : `${Math.min((slotsUsed / slotsTotal) * 100, 100)}%`,
                  background: slotsUsed >= slotsTotal
                    ? '#EF4444'
                    : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                  borderRadius: 3,
                }} />
              </div>
            )}
          </div>
        </div>

        {/* No slots warning */}
        {!hasSlots && !editId && (
          <div style={{
            background: '#FFF1F2', border: '1.5px solid #FECDD3',
            borderRadius: 14, padding: '16px 20px',
            marginBottom: 24,
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{
              width: 42, height: 42, borderRadius: 10,
              background: '#FFE4E6',
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0,
            }}>
              <Lock size={20} color="#E11D48" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontWeight: 700, color: '#9F1239', fontSize: 14,
              }}>No Job Slots Remaining</div>
              <div style={{ fontSize: 12, color: '#BE123C', marginTop: 2, lineHeight: 1.5 }}>
                You've used all {slotsTotal} slot{slotsTotal !== 1 ? 's' : ''} on your{' '}
                {plan.name} plan. Upgrade to post more jobs.
              </div>
            </div>
            <Link to="/billing" style={{
              padding: '9px 16px', borderRadius: 10,
              background: 'linear-gradient(135deg,#EF4444,#DC2626)',
              color: '#fff', fontWeight: 700, fontSize: 12,
              textDecoration: 'none', flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Zap size={13} />Upgrade Plan
            </Link>
          </div>
        )}

        {/* Step indicators */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
          {STEPS.map((s, i) => (
            <div key={i} style={{
              flex: 1, display: 'flex',
              flexDirection: 'column', alignItems: 'center', gap: 6,
            }}>
              <div style={{
                width: '100%', height: 4, borderRadius: 2,
                background: i <= step
                  ? 'linear-gradient(135deg,#4F8EF7,#7C3AED)'
                  : '#E2E8F0',
                transition: 'background 0.25s',
              }} />
              <span style={{
                fontSize: 11,
                color: i <= step ? '#4F8EF7' : '#94A3B8',
                fontWeight: i === step ? 700 : 400,
              }}>{s}</span>
            </div>
          ))}
        </div>

        {/* Card */}
        <div style={{
          background: '#fff', borderRadius: 20,
          padding: 32, border: '1px solid #E2E8F0',
          boxShadow: '0 4px 24px rgba(79,142,247,0.06)',
          opacity: !hasSlots && !editId ? 0.5 : 1,
          pointerEvents: !hasSlots && !editId ? 'none' : 'auto',
        }}>
          {error && (
            <div style={{
              background: '#FFF1F2', border: '1px solid #FECDD3',
              borderRadius: 10, padding: '10px 14px',
              marginBottom: 20,
              display: 'flex', alignItems: 'center',
              gap: 8, color: '#E11D48', fontSize: 13,
            }}>
              <AlertCircle size={16} />{error}
            </div>
          )}

          {/* ── Step 0 — Basic Info ── */}
          {step === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <SLabel>Basic Information</SLabel>

              <SInput label="Job Title *" value={form.title}
                onChange={v => set('title', v)}
                placeholder="e.g. Senior Frontend Developer" />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <SSelect label="Category *" value={form.category}
                  onChange={v => set('category', v)}
                  options={CATEGORIES.map(c => ({ value: c.value, label: c.label }))} />
                <SSelect label="Job Type *" value={form.job_type}
                  onChange={v => set('job_type', v)}
                  options={JOB_TYPES} />
              </div>

              <SInput label="Department (optional)"
                value={form.department}
                onChange={v => set('department', v)}
                placeholder="e.g. Engineering, Marketing" />

              <STextarea label="Job Description * (min 50 chars)"
                value={form.description}
                onChange={v => set('description', v)}
                placeholder="Describe the role, responsibilities, team culture..."
                rows={5} />

              {/* Responsibilities */}
              <div>
                <label style={labelStyle}>Responsibilities</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <input value={respInput}
                    onChange={e => setRespInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' &&
                      addChip('responsibilities', respInput, setRespInput)}
                    placeholder="Add responsibility and press Enter"
                    style={chipInputStyle} />
                  <button onClick={() =>
                    addChip('responsibilities', respInput, setRespInput)}
                    style={addBtnStyle}>
                    <Plus size={16} color="#4F8EF7" />
                  </button>
                </div>
                <ChipList items={form.responsibilities}
                  onRemove={i => removeChip('responsibilities', i)}
                  color="#4F8EF7" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <SInput label="Location City *" value={form.location_city}
                  onChange={v => set('location_city', v)} placeholder="New York" />
                <SInput label="Location Country *" value={form.location_country}
                  onChange={v => set('location_country', v)} placeholder="United States" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <SInput label="Work Hours" value={form.work_hours}
                  onChange={v => set('work_hours', v)} placeholder="9am - 5pm" />
                <SInput label="Working Days" value={form.working_days}
                  onChange={v => set('working_days', v)} placeholder="Mon-Fri" />
              </div>
              <SInput label="Application Deadline *" value={form.deadline}
                onChange={v => set('deadline', v)} type="date" />
            </div>
          )}

          {/* ── Step 1 — Compensation ── */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <SLabel>Compensation & Benefits</SLabel>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                <SInput label="Min Salary" value={form.salary_min}
                  onChange={v => set('salary_min', v)} placeholder="1000" type="number" />
                <SInput label="Max Salary" value={form.salary_max}
                  onChange={v => set('salary_max', v)} placeholder="2000" type="number" />
                <SSelect label="Currency" value={form.salary_currency}
                  onChange={v => set('salary_currency', v)}
                  options={CURRENCIES.map(c => ({ value: c, label: c }))} />
              </div>

              <SSelect label="Salary Period" value={form.salary_period}
                onChange={v => set('salary_period', v)}
                options={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'annual', label: 'Annual' },
                  { value: 'hourly', label: 'Hourly' },
                  { value: 'project', label: 'Per Project' },
                ]} />

              <label style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', background: '#F8FAFF',
                borderRadius: 10, border: '1px solid #E2E8F0',
                cursor: 'pointer',
              }}>
                <input type="checkbox" checked={form.is_salary_visible}
                  onChange={e => set('is_salary_visible', e.target.checked)}
                  style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 13, color: '#475569' }}>
                  Show salary to students
                </span>
              </label>

              {/* Featured Job — Pro+ only */}
              <div style={{
                padding: '14px 16px', borderRadius: 12,
                border: can.featuredJobs
                  ? '1.5px solid #7C3AED'
                  : '1.5px solid #E2E8F0',
                background: can.featuredJobs
                  ? 'rgba(124,58,237,0.04)' : '#F8FAFF',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {can.featuredJobs ? (
                      <input type="checkbox" checked={form.is_featured}
                        onChange={e => set('is_featured', e.target.checked)}
                        style={{ width: 16, height: 16 }} />
                    ) : (
                      <Lock size={14} color="#94A3B8" />
                    )}
                    <div>
                      <div style={{
                        fontSize: 13, fontWeight: 700,
                        color: can.featuredJobs ? '#1E293B' : '#94A3B8',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        <Star size={13} color={can.featuredJobs ? '#7C3AED' : '#CBD5E1'} />
                        Feature this Job Listing
                      </div>
                      <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
                        Featured jobs appear at the top of search results
                      </div>
                    </div>
                  </div>
                  {!can.featuredJobs && (
                    <Link to="/billing" style={{
                      padding: '5px 10px', borderRadius: 6,
                      background: 'linear-gradient(135deg,#7C3AED,#6D28D9)',
                      color: '#fff', fontSize: 10, fontWeight: 700,
                      textDecoration: 'none',
                    }}>
                      🚀 Pro
                    </Link>
                  )}
                </div>
              </div>

              {/* Institution/Country Targeting — Pro+ only */}
<div style={{
  padding: '14px 16px', borderRadius: 12,
  border: can.priorityPlacement
    ? '1.5px solid #10B981' : '1.5px solid #E2E8F0',
  background: can.priorityPlacement
    ? 'rgba(16,185,129,0.04)' : '#F8FAFF',
}}>
  <div style={{
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', marginBottom: can.priorityPlacement ? 12 : 0,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {can.priorityPlacement ? (
        <input type="checkbox" checked={form.is_targeted}
          onChange={e => set('is_targeted', e.target.checked)}
          style={{ width: 16, height: 16 }} />
      ) : (
        <Lock size={14} color="#94A3B8" />
      )}
      <div>
        <div style={{
          fontSize: 13, fontWeight: 700,
          color: can.priorityPlacement ? '#1E293B' : '#94A3B8',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          🎯 Target Specific Countries / Institutions
        </div>
        <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
          Only show this job to students from selected locations
        </div>
      </div>
    </div>
    {!can.priorityPlacement && (
      <Link to="/billing" style={{
        padding: '5px 10px', borderRadius: 6,
        background: 'linear-gradient(135deg,#10B981,#059669)',
        color: '#fff', fontSize: 10, fontWeight: 700,
        textDecoration: 'none',
      }}>
        🚀 Pro
      </Link>
    )}
  </div>

  {can.priorityPlacement && form.is_targeted && (
    <div>
      <label style={labelStyle}>Target Countries (comma separated)</label>
      <input
        value={form.target_countries_text || ''}
        onChange={e => set('target_countries_text', e.target.value)}
        placeholder="e.g. Pakistan, India, UAE"
        style={{
          width: '100%', padding: '10px 14px', marginTop: 6,
          border: '1.5px solid #E2E8F0', borderRadius: 10,
          fontSize: 13, outline: 'none', background: '#F8FAFF',
        }} />
      <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>
        Leave blank to show to all countries. Students are matched by their profile country.
      </div>
    </div>
  )}
</div>

              {/* Benefits */}
              <div>
                <label style={labelStyle}>Benefits</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {BENEFITS.map(b => (
                    <button key={b} onClick={() => toggleBenefit(b)} style={{
                      padding: '7px 14px', borderRadius: 20,
                      border: form.benefits.includes(b)
                        ? '1.5px solid #10B981' : '1.5px solid #E2E8F0',
                      background: form.benefits.includes(b)
                        ? 'rgba(16,185,129,0.1)' : '#fff',
                      color: form.benefits.includes(b) ? '#10B981' : '#64748B',
                      fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}>
                      {form.benefits.includes(b) ? '✓ ' : ''}{b}
                    </button>
                  ))}
                </div>
              </div>

              <SInput label="Slots Available" value={form.slots_available}
                onChange={v => set('slots_available', v)} placeholder="1" type="number" />

              <label style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', background: '#FFF7ED',
                borderRadius: 10, border: '1px solid #FED7AA',
                cursor: 'pointer',
              }}>
                <input type="checkbox" checked={form.is_urgent}
                  onChange={e => set('is_urgent', e.target.checked)}
                  style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 13, color: '#9A3412' }}>
                  🔥 Mark as Urgent (highlights listing)
                </span>
              </label>
            </div>
          )}

          {/* ── Step 2 — Requirements ── */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <SLabel>Candidate Requirements</SLabel>

              <div>
                <label style={labelStyle}>Requirements</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <input value={reqInput}
                    onChange={e => setReqInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' &&
                      addChip('requirements', reqInput, setReqInput)}
                    placeholder="Add requirement and press Enter"
                    style={chipInputStyle} />
                  <button onClick={() =>
                    addChip('requirements', reqInput, setReqInput)}
                    style={addBtnStyle}>
                    <Plus size={16} color="#4F8EF7" />
                  </button>
                </div>
                <ChipList items={form.requirements}
                  onRemove={i => removeChip('requirements', i)} color="#7C3AED" />
              </div>

              <div>
                <label style={labelStyle}>Skills Required *</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <input value={skillInput}
                    onChange={e => setSkillInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' &&
                      addChip('skills_required', skillInput, setSkillInput)}
                    placeholder="Type skill and press Enter"
                    style={chipInputStyle} />
                  <button onClick={() =>
                    addChip('skills_required', skillInput, setSkillInput)}
                    style={addBtnStyle}>
                    <Plus size={16} color="#4F8EF7" />
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {SKILLS_LIST.filter(s =>
                    !form.skills_required.includes(s)).slice(0, 16).map(s => (
                    <button key={s}
                      onClick={() => set('skills_required', [...form.skills_required, s])}
                      style={{
                        padding: '4px 10px', borderRadius: 6,
                        border: '1px solid #E2E8F0',
                        background: '#F8FAFF', color: '#64748B',
                        fontSize: 11, cursor: 'pointer',
                      }}>+ {s}</button>
                  ))}
                </div>
                <ChipList items={form.skills_required}
                  onRemove={i => removeChip('skills_required', i)} color="#4F8EF7" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <SSelect label="Target Degree" value={form.target_degree}
                  onChange={v => set('target_degree', v)}
                  options={[
                    { value: 'any', label: 'Any' },
                    { value: 'BS', label: 'BS' },
                    { value: 'BE', label: 'BE' },
                    { value: 'BBA', label: 'BBA' },
                    { value: 'MS', label: 'MS' },
                    { value: 'MBA', label: 'MBA' },
                    { value: 'PhD', label: 'PhD' },
                  ]} />
                <SSelect label="Target Year" value={form.target_year}
                  onChange={v => set('target_year', v)}
                  options={[
                    { value: 'any', label: 'Any' },
                    { value: '1', label: 'Year 1' },
                    { value: '2', label: 'Year 2' },
                    { value: '3', label: 'Year 3' },
                    { value: '4', label: 'Year 4' },
                    { value: 'graduate', label: 'Graduate' },
                  ]} />
              </div>

              <SSelect label="Experience Required"
                value={form.experience_required}
                onChange={v => set('experience_required', v)}
                options={[
                  { value: 'fresher', label: 'Fresher (No experience)' },
                  { value: '1yr', label: '1+ Year' },
                  { value: '2yr', label: '2+ Years' },
                  { value: '3yr', label: '3+ Years' },
                  { value: '5yr', label: '5+ Years' },
                ]} />
            </div>
          )}

          {/* ── Step 3 — Preview ── */}
          {step === 3 && (
            <div>
              <SLabel>Review Your Job Posting</SLabel>
              <div style={{
                background: '#F8FAFF', borderRadius: 14,
                padding: 20, border: '1px solid #E2E8F0', marginTop: 12,
              }}>
                {[
                  ['Title', form.title],
                  ['Category', form.category],
                  ['Type', form.job_type],
                  ['Location', `${form.location_city}, ${form.location_country}`],
                  ['Salary', form.salary_min
                    ? `${form.salary_currency} ${form.salary_min}–${form.salary_max || '?'} / ${form.salary_period}`
                    : 'Not specified'],
                  ['Deadline', form.deadline],
                  ['Slots', form.slots_available],
                  ['Skills', form.skills_required.slice(0, 5).join(', ')],
                  ['Featured', can.featuredJobs && form.is_featured ? '⭐ Yes' : 'No'],
                  ['Urgent', form.is_urgent ? '🔥 Yes' : 'No'],
                ].map(([k, v]) => (
                  <div key={k} style={{
                    display: 'flex', justifyContent: 'space-between',
                    padding: '8px 0',
                    borderBottom: '1px solid #E2E8F0',
                    fontSize: 13,
                  }}>
                    <span style={{ color: '#94A3B8' }}>{k}</span>
                    <span style={{
                      color: '#1E293B', fontWeight: 600,
                      maxWidth: 260, textAlign: 'right',
                    }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{
                background: '#EFF6FF', border: '1px solid #BFDBFE',
                borderRadius: 10, padding: '12px 14px',
                marginTop: 14, fontSize: 13, color: '#1D4ED8', lineHeight: 1.6,
              }}>
                ✅ Once posted, students from your target institutions
                will be able to view and apply. You'll receive
                notifications for each application.
              </div>
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 10, marginTop: 28 }}>
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)} style={{
                flex: 1, padding: 13,
                border: '1px solid #E2E8F0', borderRadius: 12,
                background: '#fff', color: '#64748B',
                fontWeight: 600, fontSize: 14, cursor: 'pointer',
                display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: 6,
              }}>
                <ChevronLeft size={16} />Back
              </button>
            )}
            <button
              onClick={step < STEPS.length - 1
                ? () => { if (validateStep()) setStep(s => s + 1); }
                : handleSubmit}
              disabled={loading || (!hasSlots && !editId)}
              style={{
                flex: 2, padding: 13, border: 'none',
                borderRadius: 12,
                background: loading || (!hasSlots && !editId)
                  ? '#CBD5E1'
                  : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                color: '#fff', fontWeight: 700, fontSize: 14,
                cursor: loading || (!hasSlots && !editId)
                  ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: 6,
                boxShadow: loading || (!hasSlots && !editId)
                  ? 'none' : '0 4px 16px rgba(79,142,247,0.3)',
              }}>
              {loading ? (
                <div style={{
                  width: 18, height: 18,
                  border: '2px solid rgba(255,255,255,0.4)',
                  borderTopColor: '#fff', borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
              ) : step < STEPS.length - 1 ? (
                <>Continue <ChevronRight size={16} /></>
              ) : (
                <><CheckCircle size={16} />
                  {editId ? 'Update Job' : 'Post Job'}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

// ── Helpers ───────────────────────────────────────────
const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: '#475569', marginBottom: 6, letterSpacing: 0.3,
};

function SLabel({ children }) {
  return (
    <div style={{
      fontWeight: 800, fontSize: 16, color: '#1E293B',
      borderBottom: '1px solid #F1F5F9', paddingBottom: 12,
    }}>{children}</div>
  );
}

function SInput({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input type={type} value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '11px 14px',
          border: '1.5px solid #E2E8F0', borderRadius: 10,
          fontSize: 14, color: '#1E293B', background: '#F8FAFF',
          outline: 'none',
        }}
        onFocus={e => e.target.style.borderColor = '#4F8EF7'}
        onBlur={e => e.target.style.borderColor = '#E2E8F0'} />
    </div>
  );
}

function SSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', padding: '11px 14px',
          border: '1.5px solid #E2E8F0', borderRadius: 10,
          fontSize: 14, color: value ? '#1E293B' : '#94A3B8',
          background: '#F8FAFF', cursor: 'pointer', outline: 'none',
        }}
        onFocus={e => e.target.style.borderColor = '#4F8EF7'}
        onBlur={e => e.target.style.borderColor = '#E2E8F0'}>
        <option value="">Select...</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function STextarea({ label, value, onChange, placeholder, rows = 3 }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <textarea value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} rows={rows}
        style={{
          width: '100%', padding: '11px 14px',
          border: '1.5px solid #E2E8F0', borderRadius: 10,
          fontSize: 14, color: '#1E293B', background: '#F8FAFF',
          resize: 'vertical', lineHeight: 1.6, outline: 'none',
        }}
        onFocus={e => e.target.style.borderColor = '#4F8EF7'}
        onBlur={e => e.target.style.borderColor = '#E2E8F0'} />
    </div>
  );
}

function ChipList({ items, onRemove, color }) {
  if (!items.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
      {items.map((item, i) => (
        <span key={i} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', borderRadius: 6,
          background: `${color}15`, border: `1px solid ${color}40`,
          color, fontSize: 12, fontWeight: 600,
        }}>
          {item}
          <button onClick={() => onRemove(i)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 0, color, display: 'flex', alignItems: 'center',
          }}>
            <X size={12} />
          </button>
        </span>
      ))}
    </div>
  );
}

const chipInputStyle = {
  flex: 1, padding: '9px 14px',
  border: '1.5px solid #E2E8F0', borderRadius: 10,
  fontSize: 13, color: '#1E293B', background: '#F8FAFF',
  outline: 'none',
};

const addBtnStyle = {
  width: 38, height: 38, border: '1.5px solid #E2E8F0',
  borderRadius: 10, background: '#F8FAFF',
  cursor: 'pointer', display: 'flex',
  alignItems: 'center', justifyContent: 'center',
};